import { EmailProvider, NotificationProviderSettings } from 'apis/notification-providers';
import { Badge, Box, Button, Flex, HStack, Radio, RadioGroup, Stack, Text, WhiteCard } from 'design-system';
import { emailProviderLabels } from 'pages/notification-providers/utils/provider-order';
import { useState } from 'react';
import { SettingSourceText } from './setting-source-text';

interface EmailProviderCardProps {
  settings: NotificationProviderSettings['email'];
  canUpdate: boolean;
  isSaving: boolean;
  onSave: (provider: EmailProvider | null) => void;
}

/** Which provider delivers every email (order emails and sign-in codes). */
export const EmailProviderCard = ({ settings, canUpdate, isSaving, onSave }: EmailProviderCardProps) => {
  const [draft, setDraft] = useState<EmailProvider>(settings.provider);
  // Start the draft over when the server's provider changes (after a save or a reset), never otherwise.
  const [draftOf, setDraftOf] = useState(settings.provider);
  if (draftOf !== settings.provider) {
    setDraftOf(settings.provider);
    setDraft(settings.provider);
  }

  return (
    <WhiteCard p='lg'>
      <Stack spacing='md'>
        <Box>
          <Text fontWeight='semibold' fontSize='lg'>
            Emails
          </Text>
          <Text fontSize='sm' color='gray.500'>
            The provider that delivers order emails and sign-in codes by email. The emails look the same with any
            provider.
          </Text>
        </Box>

        <RadioGroup value={draft} onChange={(value) => setDraft(value as EmailProvider)}>
          <Stack spacing='sm'>
            {settings.available.map((provider) => (
              <HStack key={provider.id} spacing='sm'>
                <Radio value={provider.id} isDisabled={!canUpdate || !provider.configured}>
                  {emailProviderLabels[provider.id]}
                </Radio>
                {!provider.configured && <Badge colorScheme='orange'>No key on the server</Badge>}
              </HStack>
            ))}
          </Stack>
        </RadioGroup>

        <SettingSourceText
          source={settings.source}
          envSettingName='EMAIL_PROVIDER'
          updatedAt={settings.updatedAt}
          updatedBy={settings.updatedBy}
        />

        {canUpdate && (
          <Flex justify='flex-end' gap='sm' wrap='wrap'>
            {settings.source === 'cms' && (
              <Button size='sm' variant='outline' isDisabled={isSaving} onClick={() => onSave(null)}>
                Use the server setting
              </Button>
            )}
            <Button
              size='sm'
              colorScheme='red'
              isLoading={isSaving}
              isDisabled={draft === settings.provider}
              onClick={() => onSave(draft)}>
              Save provider
            </Button>
          </Flex>
        )}
      </Stack>
    </WhiteCard>
  );
};
